import { useState, useEffect } from "react";
import { Add, Trash, Image, CloseCircle } from "iconsax-react";

export const ProductForm = ({ formData, onChange }) => {
  const [benefits, setBenefits] = useState(
    formData.benefits || [{ title: "", description: "" }],
  );
  const [faqs, setFaqs] = useState(
    formData.faqs || [{ question: "", answer: "" }],
  );

  useEffect(() => {
    if (formData.benefits) {
      setBenefits(formData.benefits);
    }
    if (formData.faqs) {
      setFaqs(formData.faqs);
    }
  }, [formData.benefits, formData.faqs]);

  const handleInputChange = (field, value) => {
    const updates = { [field]: value };

    if (field === "name") {
      updates.slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    }

    onChange({ ...formData, ...updates });
  };

  const handleBenefitChange = (index, field, value) => {
    const updatedBenefits = [...benefits];
    updatedBenefits[index][field] = value;
    setBenefits(updatedBenefits);
    onChange({ ...formData, benefits: updatedBenefits });
  };

  const addBenefit = () => {
    const newBenefits = [...benefits, { title: "", description: "" }];
    setBenefits(newBenefits);
    onChange({ ...formData, benefits: newBenefits });
  };

  const removeBenefit = (index) => {
    const updatedBenefits = benefits.filter((_, i) => i !== index);
    setBenefits(updatedBenefits);
    onChange({ ...formData, benefits: updatedBenefits });
  };

  const handleFaqChange = (index, field, value) => {
    const updatedFaqs = [...faqs];
    updatedFaqs[index][field] = value;
    setFaqs(updatedFaqs);
    onChange({ ...formData, faqs: updatedFaqs });
  };

  const addFaq = () => {
    const newFaqs = [...faqs, { question: "", answer: "" }];
    setFaqs(newFaqs);
    onChange({ ...formData, faqs: newFaqs });
  };

  const removeFaq = (index) => {
    const updatedFaqs = faqs.filter((_, i) => i !== index);
    setFaqs(updatedFaqs);
    onChange({ ...formData, faqs: updatedFaqs });
  };

  const handleImageUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange({
          ...formData,
          [field]: {
            file: file,
            preview: reader.result,
            name: file.name,
          },
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = (field) => {
    onChange({ ...formData, [field]: null });
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="space-y-4">
        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name || ""}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter product name"
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Slug <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center">
            <span className="text-[14px] text-gray-500 bg-gray-100 px-3 py-2 border border-r-0 border-gray-300 rounded-l-md">
              aims.com/products/
            </span>
            <input
              type="text"
              value={formData.slug || ""}
              onChange={(e) =>
                handleInputChange(
                  "slug",
                  e.target.value.toLowerCase().replace(/\s+/g, "-"),
                )
              }
              placeholder="product-name"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-r-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>



      <div className="space-y-4">
        <div>
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Short Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.shortDescription || ""}
            onChange={(e) =>
              handleInputChange("shortDescription", e.target.value)
            }
            placeholder="e.g., Reset your metabolism and burn more calories"
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Hero Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.heroDescription || ""}
            onChange={(e) =>
              handleInputChange("heroDescription", e.target.value)
            }
            placeholder="Enter a compelling hero description..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        <div>
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            About This Product <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.aboutProduct || ""}
            onChange={(e) => handleInputChange("aboutProduct", e.target.value)}
            placeholder="Describe the product in detail..."
            rows={6}
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>
      </div>

      <div className="space-y-4 lg:space-y-0 flex flex-col lg:flex-row gap-3 items-center">
        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Hero Image <span className="text-gray-400 font-normal text-[12px] ml-1">(Recommended: 1080 x 1350 px)</span> <span className="text-red-500">*</span>
          </label>
          {formData.heroImage ? (
            <div className="relative border-2 border-gray-300 rounded-md p-4">
              <img
                src={formData.heroImage.preview}
                alt="Hero preview"
                className="w-full h-48 object-cover rounded-md"
              />
              <button
                type="button"
                onClick={() => handleImageRemove("heroImage")}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              >
                <CloseCircle size={24} variant="Bold" />
              </button>
              <p className="text-[12px] text-gray-600 mt-2 truncate">
                {formData.heroImage.name}
              </p>
            </div>
          ) : (
            <label className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-primary transition-colors cursor-pointer block">
              <Image size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-[14px] text-gray-600 mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-[12px] text-gray-500">
                PNG, JPG, GIF up to 10MB
              </p>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => handleImageUpload("heroImage", e)}
              />
            </label>
          )}
        </div>

        <div className="w-full">
          <label className="block text-[14px] font-medium text-gray-700 mb-1">
            Benefit Image <span className="text-gray-400 font-normal text-[12px] ml-1">(Recommended: 600 x 600 px)</span>
          </label>
          {formData.benefitImage ? (
            <div className="relative border-2 border-gray-300 rounded-md p-4">
              <img
                src={formData.benefitImage.preview}
                alt="Benefit preview"
                className="w-full h-48 object-cover rounded-md"
              />
              <button
                type="button"
                onClick={() => handleImageRemove("benefitImage")}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              >
                <CloseCircle size={24} variant="Bold" />
              </button>
              <p className="text-[12px] text-gray-600 mt-2 truncate">
                {formData.benefitImage.name}
              </p>
            </div>
          ) : (
            <label className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-primary transition-colors cursor-pointer block">
              <Image size={32} className="mx-auto text-gray-400 mb-2" />
              <p className="text-[14px] text-gray-600 mb-1">
                Click to upload or drag and drop
              </p>
              <p className="text-[12px] text-gray-500">
                PNG, JPG, GIF up to 10MB
              </p>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => handleImageUpload("benefitImage", e)}
              />
            </label>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <h2 className="text-base font-medium text-gray-900">Benefits</h2>
          <p className="text-[#6D6D6D] text-sm">
            A list of concise benefit cards — title + short description each
          </p>
        </div>

        {benefits.map((benefit, index) => (
          <div
            key={index}
            className={`relative px-4 flex flex-col lg:flex-row gap-x-3 ${index < benefits.length - 1 && "border-b border-dashed pb-3"}`}
          >
            {benefits.length > 1 && (
              <button
                type="button"
                onClick={() => removeBenefit(index)}
                className="absolute top-0 -right-2 text-red-500 hover:text-red-700"
              >
                <Trash size={18} />
              </button>
            )}

            <input
              type="text"
              value={benefit.title}
              onChange={(e) =>
                handleBenefitChange(index, "title", e.target.value)
              }
              placeholder="Enter benefit title"
              className="w-full p-2 h-9 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />

            <textarea
              value={benefit.description}
              onChange={(e) =>
                handleBenefitChange(index, "description", e.target.value)
              }
              placeholder="Enter benefit description"
              rows={3}
              className="w-full p-2 h-9 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={addBenefit}
          className="flex items-center gap-2 px-3 py-2 rounded-full text-[14px] font-medium text-primary bg-[#27272A0F] hover:bg-[#27272A0F]/10 transition-colors"
        >
          <Add size={18} />
          Add More
        </button>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <h2 className="text-base font-medium text-gray-900">FAQs</h2>
          <p className="text-[#6D6D6D] text-sm">
            Include a few helpful questions and answers if needed
          </p>
        </div>

        {faqs.map((faq, index) => (
          <div
            key={index}
            className={`relative px-4 flex flex-col lg:flex-row gap-x-3 ${index < faqs.length - 1 && "border-b border-dashed pb-3"}`}
          >
            {faqs.length > 1 && (
              <button
                type="button"
                onClick={() => removeFaq(index)}
                className="absolute top-0 -right-2 text-red-500 hover:text-red-700"
              >
                <Trash size={18} />
              </button>
            )}

            <input
              type="text"
              value={faq.question || ""}
              onChange={(e) =>
                handleFaqChange(index, "question", e.target.value)
              }
              placeholder="Enter faq question"
              className="w-full p-2 h-9 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />

            <textarea
              value={faq.answer || ""}
              onChange={(e) => handleFaqChange(index, "answer", e.target.value)}
              placeholder="Enter faq answer"
              rows={3}
              className="w-full p-2 h-9 border border-gray-300 rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={addFaq}
          className="flex items-center gap-2 px-3 py-2 rounded-full text-[14px] font-medium text-primary bg-[#27272A0F] hover:bg-[#27272A0F]/10 transition-colors"
        >
          <Add size={18} />
          Add More
        </button>
      </div>
    </div>
  );
};
